import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

// Redux
import { connect } from "react-redux";

// Components
import IkTable from "@Components/IkTable";
import HistoricChatModal from "../../Modals/HistoricChatModal";

// Semantic
import { Popup, Icon } from "semantic-ui-react";

// Lodash
import { isEmpty, trim, slice, replace, find } from "lodash";

// Utils
import dataHandler from "@Utils/dataHandler";
import { phoneFormat } from "@Utils/formats";

// Styles
import styles from "./styles.module.css";

const Detalladas = ({ dispatch, userName, companyName, onOpenFilterModal, appliedFilters = {}, selectedFields, listItemsSelected }) => {
	const defaultParams = {
		page: 1,
		orderKey: appliedFilters.orderKey || (appliedFilters.fieldDate === "createdDate" ? "createdDate" : "closeDate"),
		orderDir: appliedFilters.orderDir || "desc",
		rows: 20,
	};
	const [filteredItems, setFilteredItems] = useState([]);
	const [filteredCount, setFilteredCount] = useState(0);
	const [filteredPrintData, setFilteredPrintData] = useState([]);
	const [lastParams, setLastParams] = useState(defaultParams);
	const [selectedChatData, setSelectedChatData] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	useEffect(() => {
		const mergedParams = {
			...defaultParams,
			...appliedFilters,
			fields:
				"closeDate,group.name,user.fullName,tags.name,contact.phone,contact.name,createdDate,channel.label,channel:platform.name,closeState,closeTab,contact.companyContactFields",
		};

		(async () => {
			try {
				const response = await dataHandler({
					dispatch,
					method: "GET",
					url: "/reports/historic/detailed",
					params: mergedParams,
				});

				const transformedItems = response.items.map((item) => {
					return {
						...item,
						tags: item.tags || [],
						group: item.group || {},
					};
				});

				setFilteredItems(transformedItems);
				setFilteredCount(response.count);
				setLastParams(mergedParams);
			} catch (error) {
				setFilteredItems([]);
				setFilteredCount(0);
			}
		})();
	}, [appliedFilters]);

	const handlerData = async (params = defaultParams, printable) => {
		const mergedParams = {
			...appliedFilters,
			...params,
			fields:
				"closeDate,group.name,user.fullName,tags.name,contact.phone,contact.name,createdDate,channel.label,channel:platform.name,closeState,closeTab,contact.companyContactFields",
		};

		const data = await dataHandler({
			method: "GET",
			dispatch,
			url: "/reports/historic/detailed",
			params: mergedParams,
		});

		const transformedItems = data.items.map((item) => {
			return {
				...item,
				tags: item.tags || [],
				group: item.group || {},
			};
		});

		if (printable) {
			setFilteredPrintData(transformedItems);
		} else {
			setFilteredItems(transformedItems);
			setFilteredCount(data.count);
			setLastParams(mergedParams);
		}
	};

	const handleRefreshData = () => {
		handlerData(lastParams);
	};

	const renderTopButtons = () => {
		const buttons = [
			{
				icon: "filter",
				className:
					Object.keys(typeof appliedFilters !== "undefined" && appliedFilters ? appliedFilters : {}).length > 0
						? styles.filterButtonActive
						: styles.filterButton,
				onClick: onOpenFilterModal,
				content: Object.keys(typeof appliedFilters !== "undefined" && appliedFilters ? appliedFilters : {}).length > 0 ? "Filtrando" : "",
				label: Object.keys(typeof appliedFilters !== "undefined" && appliedFilters ? appliedFilters : {}).length > 0 ? "Filtrando" : "",
				showLabel: true,
				componentType: "button",
			},
			{
				icon: "refresh",
				className: styles.refreshButton,
				onClick: handleRefreshData,
			},
		];

		const filters = typeof appliedFilters !== "undefined" && appliedFilters ? appliedFilters : {};
		const dateRange = filters.createdDate || filters.closeDate;
		if (dateRange) {
			const formatDate = (d) => {
				if (!d) return "";
				const parts = d.split("-");
				if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
				return d;
			};
			const [start, end] = dateRange.split(",");
			let value = formatDate(start);
			const today = new Date();
			const currentEnd =
				end || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
			if (currentEnd && start !== currentEnd) value += ` al ${formatDate(currentEnd)}`;

			buttons.push({
				componentType: "Label",
				title: "Fecha:",
				value,
			});
		}

		return buttons;
	};

	const truncateString = (str, num) => {
		if (isEmpty(str)) {
			return null;
		}
		const icon = (
			<span key={1} className={styles.icon}>
				<Popup position="bottom right" content={str} trigger={<Icon name="comment alternate outline" style={{ color: "#286b92" }} />} />
			</span>
		);
		if (num >= str.length) {
			return str;
		}
		return <div className={styles.noWrap}>{[`${slice([...String(str)], 0, num).join("")}...`, icon]}</div>;
	};

	const norm = (s) => {
		return trim(String(s ?? "").toLowerCase());
	};
	const cleanListValue = (v) => {
		return trim(String(v ?? "").replace(/§/g, ""));
	};

	const findListItemValue = (fields = [], itemLabel) => {
		const L = norm(itemLabel);
		for (const cf of fields) {
			if (cf?.type === "list" && Array.isArray(cf.items)) {
				const row = cf.items.find((it) => {
					return norm(it?.label) === L;
				});
				if (row) return row;
			}
		}
		return null;
	};

	const generateDynamicColumns = () => {
		const cols = [];

		(selectedFields || []).forEach((fieldLabel) => {
			const key = String(fieldLabel).replace(/[^\w]+/g, "_");
			cols.push({
				label: fieldLabel,
				key,
				format: "string",
				headerPosition: "center",
				dataPosition: "left",
				width: 1,
				canSort: false,
				canFilter: false,
				stringLength: 40,
				render: (item, { setOpentImagePreview } = {}) => {
					const f = find(item.contact?.companyContactFields || [], (cf) => {
						return norm(cf.label) === norm(fieldLabel);
					});
					let value = f ? f.value : "";
					if (f && f.type === "image" && value) {
						return (
							<Icon
								name="image"
								size="small"
								style={{ maxWidth: "50px", maxHeight: "50px", cursor: "pointer" }}
								onClick={() => {
									if (setOpentImagePreview) {
										setOpentImagePreview({
											isOpen: true,
											src: value,
										});
									}
								}}
							/>
						);
					}
					if (typeof value === "object" && value !== null) {
						if (f.type === "direction") {
							value =
								value.formattedAddress ||
								[value.street, value.streetNumber, value.zone, value.sector, value.city, value.province].filter(Boolean).join(", ");
						} else {
							value = JSON.stringify(value);
						}
					}

					if (f && f.type === "bool") {
						if (value === true || String(value).toLowerCase() === "true") {
							value = "Si";
						} else if (value === false || String(value).toLowerCase() === "false") {
							value = "No";
						}
					}

					if (f && f.type === "number") {
						value = phoneFormat(value);
					}

					return truncateString(value, 40);
				},
			});
		});

		(listItemsSelected || []).forEach((itemLabelRaw) => {
			const itemLabel = trim(String(itemLabelRaw));
			if (!itemLabel) return;
			const key = `li_${replace(String(itemLabel), /[^\w]+/g, "_")}`;
			cols.push({
				label: itemLabel,
				key,
				format: "string",
				headerPosition: "center",
				dataPosition: "left",
				width: 1,
				stringLength: 40,
				canSort: false,
				canFilter: false,
				render: (item, { setOpentImagePreview } = {}) => {
					const fields = item.contact?.companyContactFields || [];
					const row = findListItemValue(fields, itemLabel);
					let value = row ? row.value : "";

					if (row && row.type === "image" && value) {
						return (
							<Icon
								name="image"
								size="small"
								style={{ maxWidth: "50px", maxHeight: "50px", cursor: "pointer" }}
								onClick={() => {
									if (setOpentImagePreview) {
										setOpentImagePreview({
											isOpen: true,
											src: value,
										});
									}
								}}
							/>
						);
					}

					if (typeof value === "object" && value !== null) {
						if (row.type === "direction") {
							value =
								value.formattedAddress ||
								[value.street, value.streetNumber, value.zone, value.sector, value.city, value.province].filter(Boolean).join(", ");
						} else {
							value = JSON.stringify(value);
						}
					}

					if (row && row.type === "bool") {
						if (value === true || String(value).toLowerCase() === "true") {
							value = "Si";
						} else if (value === false || String(value).toLowerCase() === "false") {
							value = "No";
						}
					}

					if (row && row.type === "number") {
						value = phoneFormat(value);
					}

					if (typeof value === "string" && row?.type !== "bool") {
						value = cleanListValue(value);
					}

					return truncateString(value, 40);
				},
			});
		});

		return cols;
	};

	const handleChatClick = useCallback((item) => {
		setSelectedChatData(item);
		setIsModalOpen(true);
	}, []);

	const handleChatKeyDown = useCallback((e, item) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			setSelectedChatData(item);
			setIsModalOpen(true);
		}
	}, []);

	const columns = [
		{
			label: "Chat ID",
			key: "id",
			format: "string",
			headerPosition: "center",
			dataPosition: "center",
			width: 1,
			render: (item) => {
				const id = item.chatId || item.id;
				return (
					<span
						className={styles.clickableChatId}
						role="button"
						tabIndex={0}
						onClick={() => {
							return handleChatClick(item);
						}}
						onKeyDown={(e) => {
							return handleChatKeyDown(e, item);
						}}>
						{id}
					</span>
				);
			},
		},
		{
			label: "Plataforma",
			key: "channel.platform.name",
			format: "string",
			headerPosition: "center",
			dataPosition: "center",
			width: 1,
		},
		{
			label: "Cuenta",
			key: "channel.label",
			format: "string",
			headerPosition: "center",
			dataPosition: "left",
			width: 1,
		},
		{ label: "Grupos", key: "group.name", format: "string", headerPosition: "center", dataPosition: "left", width: 1 },
		{
			label: "Usuario",
			key: "user.fullName",
			format: "string",
			headerPosition: "center",
			dataPosition: "left",
			width: 1,
		},
		{ label: "Contacto", key: "contact.name", format: "string", headerPosition: "center", dataPosition: "left", width: 1 },
		{ label: "Teléfono", key: "contact.phone", format: "phone", headerPosition: "center", dataPosition: "center", width: 1 },
		{ label: "F. Entrada", key: "createdDate", format: "dateTime", headerPosition: "center", dataPosition: "center", width: 1 },
		{ label: "F. Cierre", key: "closeDate", format: "dateTime", headerPosition: "center", dataPosition: "center", width: 1 },
		{
			label: "Tiempo Total",
			key: "createdDate",
			endDateKey: "closeDate",
			format: "timeBetween",
			headerPosition: "center",
			dataPosition: "left",
			width: 1,
		},
		{
			label: "Tab",
			key: "closeTab",
			format: "string",
			headerPosition: "center",
			dataPosition: "left",
			width: 1,
		},
		{
			label: "Estado",
			key: "closeState",
			format: "string",
			headerPosition: "center",
			dataPosition: "left",
			width: 1,
		},
		{
			label: "Etiquetas",
			key: "tags.name[]?",
			format: "arrayTruncate",
			headerPosition: "left",
			canSort: false,
			dataPosition: "left",
			width: 1,
		},
		...generateDynamicColumns(),
	];

	return (
		<div className={styles.container}>
			<div className={styles.containerTableGroup}>
				<div className={styles.tableGroupList}>
					<IkTable
						data={filteredItems}
						columns={columns}
						totalRows={filteredCount}
						filteredPrintData={filteredPrintData}
						defaultParams={defaultParams}
						dataHandler={handlerData}
						enableScroll
						// widthAuto
						print={{
							printHeader: {
								company: companyName,
								title: "Detalladas",
								user: userName,
							},
						}}
						searcher={{
							searchable: true,
							searchPosition: "flex-start",
							searchDisabled: false,
							filterButton: false,
							width: "40rem",
						}}
						topButtons={renderTopButtons()}
					/>
				</div>
			</div>
			{isModalOpen && (
				<HistoricChatModal
					open={isModalOpen}
					onClose={() => {
						setIsModalOpen(false);
						setSelectedChatData(null);
					}}
					chatData={selectedChatData}
					companyName={companyName}
					userName={userName}
				/>
			)}
		</div>
	);
};

function mapStateToProps({ user, company }) {
	return {
		userName: user.name,
		companyName: company.name,
	};
}

Detalladas.propTypes = {
	companyName: PropTypes.string,
	userName: PropTypes.string,
	dispatch: PropTypes.func,
	onOpenFilterModal: PropTypes.func,
	appliedFilters: PropTypes.object,
	selectedFields: PropTypes.array,
	listItemsSelected: PropTypes.array,
};

export default connect(mapStateToProps)(Detalladas);
